import {
  useCallback,
  useEffect,
  useMemo,
  useRef
} from "react";

import { resolveCurriculumVoiceTrackId } from "@/audio/curriculum-catalog";
import { LEVEL_REGISTRY } from "@/content/registry";
import { useAudio } from "@/features/audio";
import type { OfflinePlaySessionIdentity } from "@/features/offline";
import {
  PlayOrchestrator,
  type PlayAttemptLifecycleEvent,
  type PlayAudioCommands
} from "@/features/play";
import { usePrefersReducedMotion } from "@/platform/accessibility";

import type { AppPlayAdapterProps } from "./contracts";
import { appOfflineCoordinator } from "./offline-coordinator";
import { APP_PLAY_RENDERERS } from "./play-renderers";
import { createAppPlaySessionSaver } from "./play-session-saver";
import { createNarrationCoordinator } from "./narration-coordinator";

let offlineSessionSequence = 0;

function nextOfflineSessionToken(): number {
  offlineSessionSequence += 1;
  return offlineSessionSequence;
}

function lifecycleKey(event: PlayAttemptLifecycleEvent): string {
  return (
    event.profileId +
    ":" +
    event.level.id +
    ":" +
    String(event.attemptNumber)
  );
}

export function DefaultPlayAdapter({
  levelId,
  navigation,
  persistenceMode,
  repository,
  repositoryGeneration,
  selectedProfile
}: AppPlayAdapterProps) {
  const { commands, snapshot } = useAudio();
  const reducedMotion = usePrefersReducedMotion();
  const offlineIdentitiesRef = useRef(
    new Map<string, OfflinePlaySessionIdentity>()
  );
  const sessionSaver = useMemo(
    () => {
      if (!Number.isSafeInteger(repositoryGeneration)) {
        throw new Error("Invalid repository generation for Play.");
      }

      return createAppPlaySessionSaver({
        isPersistent: persistenceMode === "local",
        recommendationLevels: LEVEL_REGISTRY,
        repository
      });
    },
    [persistenceMode, repository, repositoryGeneration]
  );
  const resolvedLevel = useMemo(
    () =>
      levelId === null
        ? null
        : LEVEL_REGISTRY.find(({ id }) => id === levelId) ?? null,
    [levelId]
  );

  const narrationCoordinator = useMemo(
    () =>
      createNarrationCoordinator({
        playVoice: commands.playVoice,
        stopVoice: commands.stopVoice,
        unlock: commands.unlock
      }),
    [commands]
  );

  const audioCommands = useMemo<PlayAudioCommands>(
    () => ({
      playNarration: (request) => {
        const trackId =
          request.audioPath === null
            ? null
            : resolveCurriculumVoiceTrackId(request.audioPath);
        return trackId === null
          ? undefined
          : narrationCoordinator.request(trackId);
      },
      replayNarration: (request) => {
        const trackId =
          request.audioPath === null
            ? null
            : resolveCurriculumVoiceTrackId(request.audioPath);
        return trackId === null
          ? undefined
          : narrationCoordinator.request(trackId);
      },
      playFeedback: (kind) => narrationCoordinator.request(kind),
      playSuccess: () =>
        Promise.all([
          narrationCoordinator.request("success"),
          commands.playSfx("success")
        ]),
      stopNarration: narrationCoordinator.cancel
    }),
    [commands, narrationCoordinator]
  );

  useEffect(() => {
    if (snapshot.unlockState === "unlocked") {
      void narrationCoordinator.notifyUnlocked();
    }
  }, [narrationCoordinator, snapshot.unlockState]);

  useEffect(
    () => () => {
      narrationCoordinator.cancel();
    },
    [levelId, narrationCoordinator]
  );

  useEffect(() => {
    if (snapshot.unlockState !== "unlocked") {
      return;
    }

    void commands.playMusic(resolvedLevel?.domain ?? "map");
  }, [commands, resolvedLevel?.domain, snapshot.unlockState]);

  useEffect(
    () => () => {
      commands.stopMusic();
    },
    [commands]
  );

  const handleAttemptStart = useCallback(
    (event: PlayAttemptLifecycleEvent) => {
      const identity: OfflinePlaySessionIdentity = {
        domain: event.level.domain,
        profileId: event.profileId,
        sessionToken: nextOfflineSessionToken()
      };
      offlineIdentitiesRef.current.set(lifecycleKey(event), identity);
      appOfflineCoordinator.notifyBoundary({ kind: "play", ...identity });
    },
    []
  );

  const finishOfflineAttempt = useCallback(
    (kind: "map" | "result", event: PlayAttemptLifecycleEvent) => {
      const key = lifecycleKey(event);
      const identity = offlineIdentitiesRef.current.get(key);
      if (identity === undefined) {
        return;
      }

      offlineIdentitiesRef.current.delete(key);
      appOfflineCoordinator.notifyBoundary({ kind, ...identity });
    },
    []
  );

  const handleAttemptExit = useCallback(
    (event: PlayAttemptLifecycleEvent) => {
      finishOfflineAttempt("map", event);
    },
    [finishOfflineAttempt]
  );
  const handleAttemptResult = useCallback(
    (event: PlayAttemptLifecycleEvent) => {
      finishOfflineAttempt("result", event);
    },
    [finishOfflineAttempt]
  );

  return (
    <PlayOrchestrator
      audioCommands={audioCommands}
      levelId={levelId}
      levelRegistry={LEVEL_REGISTRY}
      narrationCaption={snapshot.caption}
      onAttemptExit={handleAttemptExit}
      onAttemptResult={handleAttemptResult}
      onAttemptStart={handleAttemptStart}
      onPlayLevel={navigation.showLevel}
      onReturnToMap={navigation.showMap}
      profileId={selectedProfile.id}
      reducedMotion={reducedMotion}
      rendererRegistry={APP_PLAY_RENDERERS}
      sessionSaver={sessionSaver}
    />
  );
}
